import { Alert, Heading, Spinner } from '@digitools/honest-react';
import { Breadcrumb, Breadcrumbs } from '@digitools/honest-ui';
import queryString from 'query-string';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import EmployeeDetails from 'status/components/EmployeeDetails/EmployeeDetails';
import { useAuth0, useProductAttributeCodes, useStatusDefinitionList } from 'status/hooks';
import { decode } from 'status/utilities/hash';
import titleCase from 'status/utilities/titleCase';
import { TEALIUM_PAGE_VIEW } from '../../../../../constants/tealiumConstants';
import useAuthentication from '../../../../../hooks/useAuthentication';
import useGet from '../../../../../hooks/useGet';
import useTealium from '../../../../../hooks/useTealium';
import CommunicationPreferencesLink from '../../CommunicationPreferences/CommunicationPreferencesLink';
import ClaimInfo from './ClaimInfo';
import ClaimTabs from '../Tabs/ClaimTabs';
import { withRouter } from '../../../../../utils/withRouter';
import useExperience from 'src/hooks/useExperience';
import { Experience } from 'src/types/Experience';
import { useHomePageBreadcrumbs, useStatusBreadcrumbs } from 'src/components/Breadcrumbs';
import useTranslate from 'src/hooks/useTranslate';
import BreadcrumbText from 'src/packages/status/i18n/BreadcrumbText';
const EmployeeError = ({ error }) => (<Alert type={'error'}>
    {error.response && error.response.status === 404 ? ('No employee found.') : (<span>
        <strong>Error!</strong>&nbsp;Something went wrong when trying to load the employee.
      </span>)}
  </Alert>);
const ClaimDetails = () => {
    const location = useLocation();
    const { userMeta } = useAuth0();
    const { experience } = useExperience();
    const { user } = useAuthentication();
    const { t } = useTranslate();
    const { claimDetails } = BreadcrumbText;
    const { claimNum, empNum } = useParams();
    const decodedClaimNum = decode(claimNum || '');
    const decodedEmpNum = decode(empNum || '');
    const params = queryString.parse(location.search);
    const { trackView } = useTealium();
    const messagesEndRef = useRef(null);
    //TODO://FIXME: these two (claimStatus & SubsAndLocs filled by a separate util method 3 layers down then passed/called back up - very ugly)
    const [claimStatus, setClaimStatus] = useState();
    const [subsAndLocs, setSubsAndLocs] = useState();
    useEffect(() => {
        trackView({
            page_l3: TEALIUM_PAGE_VIEW.PAGE_L3.STATUS,
            page_l5: TEALIUM_PAGE_VIEW.PAGE_L5.STATUS.COVERAGE_DETAILS,
            page_l6: TEALIUM_PAGE_VIEW.PAGE_L6.STATUS.CLAIM_DETAILS,
        });
        scrollTo(0, 0);
    }, []);
    const { error: claimError, loading: loadingClaim, data: claimData, } = useGet({
        url: '/status-service/claims/search/searchByClaimNum',
        options: {
            params: {
                claimNum: decodedClaimNum,
            },
        },
    });
    // TODO: Unfortunately as it stands, in order to get
    // associated leave we must get all employee leaves and filter
    // this should be changed eventually!!!
    const { error: leaveError, loading: loadingLeaves, data: leaveData, } = useGet({
        url: '/status-service/leaves/search/searchByEmpNum',
        options: {
            params: {
                empNum: decodedEmpNum,
            },
        },
        noInitial: user?.customerInContext.smallMid,
    });
    const { error: employeeError, loading: loadingEmployee, data: employeeData, } = useGet({
        url: '/status-service/employee/search/searchByEmpNum',
        options: {
            params: {
                empNum: decodedEmpNum,
            },
        },
    });
    const empName = employeeData?.empFirstName && employeeData?.empLastName
        ? `${titleCase(employeeData.empFirstName)} ${titleCase(employeeData.empLastName)}`
        : null;
    const { data: notificationData } = useGet({
        url: `/mlp-ui-service/services/v1/notificationsEnable?custDiv=${user && user.customerDiv}&custSeral=${user && user.customerSerial}`,
    });
    const { data: communicationPreferencesData } = useGet({
        url: '/mlp-ui-service/services/v1/communicationPreferences',
    });
    const { error: statusDefError, loading: statusDefLoading, statusDefinitionList } = useStatusDefinitionList();
    const { error: prodAttError, loading: prodAttLoading, productAttributeCodes } = useProductAttributeCodes();
    const loading = loadingClaim || loadingLeaves || loadingEmployee || prodAttLoading || statusDefLoading;
    // Spinner if loading content
    if (loading) {
        return <Spinner id='claim-details-spinner'/>;
    }
    // Error whole page if no employee
    if (employeeError) {
        return <EmployeeError error={employeeError}/>;
    }
    const updateStatusCallback = status => {
        if (!claimStatus) {
            setClaimStatus(status);
        }
    };
    const updateSubsAndLocsCallback = value => {
        if (!subsAndLocs) {
            setSubsAndLocs(value);
        }
    };
    const basicInfo = { ...claimStatus, ...subsAndLocs };
    return (<div style={{ position: 'relative' }}>
      <div className={'d-flex flex-row flex-wrap align-items-center justify-content-between'}>
        <Breadcrumbs>
          {useHomePageBreadcrumbs()}
          {useStatusBreadcrumbs(decodedEmpNum, experience, params, location)}
          <Breadcrumb>{t(claimDetails)}</Breadcrumb>
        </Breadcrumbs>
        {experience &&
            experience === Experience.EMPLOYEE &&
            notificationData &&
            notificationData.notificationsActiveIndicator &&
            notificationData.notificationsActiveIndicator === 'true' &&
            communicationPreferencesData &&
            !communicationPreferencesData.notification && (<CommunicationPreferencesLink />)}
      </div>
      <Heading data-testid='claim-details-header'>{t(claimDetails)}</Heading>
      <EmployeeDetails empNum={decodedEmpNum} updateSubsAndLocsCallback={updateSubsAndLocsCallback} empEmailAddress={employeeData?.empEmailAddr}/>
      <br />
      <div ref={messagesEndRef}></div>
      <ClaimInfo claim={claimData} employeeName={empName} params={params} empNum={decodedEmpNum} leaveData={leaveData} 
    // @ts-ignore
    leaveError={leaveError} error={claimError} statusDefinitionList={statusDefinitionList} prodAttCodes={productAttributeCodes} claimId={decodedClaimNum} updateStatusCallback={updateStatusCallback} isSmallMidCustomer={user?.customerInContext.smallMid}/>
      <div ref={messagesEndRef}></div>
      <div className='mb-4' data-public={true}>
        <ClaimTabs empNum={decodedEmpNum} basicInfo={basicInfo} claim={claimData} userMeta={userMeta} experience={experience} claimNum={claimNum} employeeData={employeeData}/>
      </div>
    </div>);
};
export default withRouter(ClaimDetails);
